import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
        if (!user) {
          const token = localStorage.getItem('AficFreshtoken');
          const response = await axios.get('https://afric-fresh-backend.onrender.com/api/users/profile', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setUserData(response.data);
          setFormData(response.data);
        } else {
          setUserData(user);
          setFormData(user);
          
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'firstName':
      case 'lastName':
        if (!value) error = 'This field is required';
        else if (value.length < 2) error = 'Must be at least 2 characters';
        else if (!/^[a-zA-Z\s]*$/.test(value)) error = 'Only letters and spaces allowed';
        break;

      case 'email':
        if (!value) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format';
        break;

      case 'phoneNumber':
        if (!value) error = 'Phone number is required';
        else if (!/^\+?[\d\s-]{10,}$/.test(value)) error = 'Invalid phone number format';
        break;

      case 'gender':
        if (!value) error = 'Gender is required';
        break;

      case 'Birthday':
        if (!value) error = 'Birthday is required';
        else {
          const date = new Date(value);
          const today = new Date();
          if (isNaN(date.getTime())) error = 'Invalid date format';
          else if (date > today) error = 'Birthday cannot be in the future';
        }
        break;

      case 'address.streetAddress':
        if (!value) error = 'Street address is required';
        else if (value.length < 5) error = 'Address is too short';
        break;

      case 'address.city':
        if (!value) error = 'City is required';
        else if (!/^[a-zA-Z\s]*$/.test(value)) error = 'Only letters and spaces allowed';
        break;

      case 'address.countryRegion':
        if (!value) error = 'Country/Region is required';
        break;

      case 'address.zipCode':
        if (!value) error = 'Zip code is required';
        else if (!/^[0-9a-zA-Z\s-]{4,10}$/.test(value)) error = 'Invalid zip code format';
        break;

      default:
        break;
    }
    return error;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setErrors({});
    toast.success('You can now edit your profile');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate the field
    const error = validateField(name, value);
    
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Update form data
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'gender',
      'Birthday',
      'address.streetAddress',
      'address.city',
      'address.countryRegion',
      'address.zipCode'
    ];

    fieldsToValidate.forEach(field => {
      let value;
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        value = formData[parent]?.[child];
      } else {
        value = formData[field];
      }
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }
  
    const loadingToast = toast.loading('Updating profile...');
    
    try {
      const token = localStorage.getItem('AficFreshtoken');
  
      const response = await axios.put(
        'https://afric-fresh-backend.onrender.com/user/update',
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setUserData(response.data.data);
        localStorage.setItem('AficFreshuserDetails', JSON.stringify(response.data.data));
        setIsEditing(false);
        setErrors({});
        toast.dismiss(loadingToast);
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to update profile');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(userData);
    setErrors({});
    toast.success('Changes cancelled');
  };

  const renderInput = (label, name, type = 'text') => {
    // Special handling for gender select
    if (name === 'gender') {
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <select
            name={name}
            value={formData[name] || ''}
            onChange={handleChange}
            disabled={!isEditing}
            className={`w-full px-3 py-2 rounded-lg border ${
              isEditing 
                ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                : 'bg-gray-50 border-gray-200'
            } disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${errors[name] ? 'border-red-500' : ''}`}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          {errors[name] && (
            <p className="text-red-500 text-xs mt-1">{errors[name]}</p>
          )}
        </div>
      );
    }

    // Regular input rendering for other fields
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <input
          type={type}
          name={name}
          value={name.includes('.') 
            ? formData[name.split('.')[0]]?.[name.split('.')[1]] || ''
            : formData[name] || ''
          }
          onChange={handleChange}
          disabled={!isEditing}
          className={`w-full px-3 py-2 rounded-lg border ${
            isEditing 
              ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
              : 'bg-gray-50 border-gray-200'
          } disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
          ${errors[name] ? 'border-red-500' : ''}`}
        />
        {errors[name] && (
          <p className="text-red-500 text-xs mt-1">{errors[name]}</p>
        )}
      </div>
    );
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 max-sm:px-1 max-sm:mt-16 h-screen w-full">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900">Profile Information</h2>
            {!isEditing && (
              <button
                onClick={handleEdit}
                className="px-4 py-2 max-sm:py-1 max-sm:px-1 max-sm:text-sm bg-[#007D44] text-white rounded-lg hover:bg-[#38a574] transition-colors duration-200"
              >
                Edit Profile
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            {renderInput('First Name', 'firstName')}
            {renderInput('Last Name', 'lastName')}
            {renderInput('Gender', 'gender')}
            {renderInput('Birthday', 'Birthday', 'date')}
            {renderInput('Email', 'email', 'email')}
            {renderInput('Phone Number', 'phoneNumber', 'tel')}
            {renderInput('Street Address', 'address.streetAddress')}
            {renderInput('City', 'address.city')}
            {renderInput('Country/Region', 'address.countryRegion')}
            {renderInput('Zip/Postal Code', 'address.zipCode')}

            {isEditing && (
              <div className="md:col-span-2 flex gap-4 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default Profile;